// Loyalty points card on the Profile page. Shows balance + redeem action + ledger.
import { useEffect, useState } from "react";
import api from "@/api/client";
import { Star, Gift, TrendingUp, Sparkles, ArrowUpRight, ArrowDownRight } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("ar", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ""; }
}

export default function LoyaltyCard({ onRedeemed }) {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ points: 0, config: {}, equivalent_lyd: 0, ledger: [] });
  const [redeemAmount, setRedeemAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/loyalty/me");
      setState(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const redeem = async () => {
    setErr(""); setMsg("");
    const points = parseInt(redeemAmount, 10);
    if (!points || points < (state.config?.min_redeem_points || 100)) {
      setErr(`الحد الأدنى للاستبدال ${state.config?.min_redeem_points || 100} نقطة`);
      return;
    }
    if (points > state.points) {
      setErr("ليست لديك نقاط كافية");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/loyalty/redeem", { points });
      setMsg(`تم! أضفنا ${data.wallet_credit_lyd.toFixed(2)} د.ل إلى محفظتك.`);
      setRedeemAmount("");
      onRedeemed?.();
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشل الاستبدال");
    } finally { setBusy(false); }
  };

  if (loading) return (
    <div className="bg-white rounded-3xl talabya-shadow-soft p-5 animate-pulse">
      <div className="h-4 w-32 bg-gray-100 rounded"/>
      <div className="mt-4 h-16 bg-gray-100 rounded-2xl"/>
    </div>
  );

  const cfg = state.config || {};
  const rate = cfg.redemption_rate || 100;
  const minR = cfg.min_redeem_points || 100;
  const canRedeem = state.points >= minR;
  const ledger = showAll ? state.ledger : state.ledger.slice(0, 5);

  return (
    <div className="rounded-3xl overflow-hidden talabya-shadow-float" data-testid="loyalty-card">
      {/* Balance hero */}
      <div
        className="relative p-5 text-white"
        style={{ background: "linear-gradient(135deg, #7C2D12 0%, #B45309 50%, #C2410C 100%)" }}
      >
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10"/>
        <div className="absolute -bottom-14 -left-8 w-40 h-40 rounded-full bg-white/5"/>
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-white/85 text-sm">
              <Star size={16}/> نقاط الولاء
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="text-5xl font-black" style={{fontFamily:"Cairo"}} data-testid="loyalty-points-value">
                {state.points}
              </div>
              <div className="text-lg text-white/90">نقطة</div>
            </div>
            <div className="mt-1 text-xs text-white/80 flex items-center gap-1">
              <TrendingUp size={12}/>
              تعادل <span className="font-bold text-white" data-testid="loyalty-equivalent">{state.equivalent_lyd.toFixed(2)}</span> د.ل رصيد
            </div>
          </div>
          <div className="shrink-0 bg-white/15 rounded-2xl px-3 py-2 text-center">
            <div className="text-[10px] text-white/80">معدّل التحويل</div>
            <div className="font-black text-sm">{rate} نقطة</div>
            <div className="text-[10px] text-white/80">= 1 د.ل</div>
          </div>
        </div>
      </div>

      {/* Redeem block */}
      <div className="bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <Gift size={18} className="text-primary-brand"/>
          <h3 className="font-black text-base" style={{fontFamily:"Cairo"}}>استبدال النقاط بمحفظتك</h3>
        </div>
        <div className="text-xs text-[var(--text-secondary)] mb-3 leading-relaxed">
          يمكنك استبدال نقاطك مقابل رصيد يُضاف إلى محفظتك. الحد الأدنى: <span className="font-bold text-primary-brand">{minR}</span> نقطة.
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {[minR, minR * 2, minR * 5, minR * 10]
            .filter((v) => v <= state.points)
            .map((v) => (
              <button
                key={v}
                onClick={() => setRedeemAmount(String(v))}
                className="px-3 py-1.5 text-xs font-black rounded-xl bg-blue-50 text-primary-brand hover:bg-blue-100 border border-primary-brand/20"
                data-testid={`loyalty-quick-${v}`}
              >
                {v} نقطة ({(v / rate).toFixed(2)} د.ل)
              </button>
            ))}
        </div>
        <div className="flex gap-2">
          <input
            type="number"
            inputMode="numeric"
            value={redeemAmount}
            onChange={(e) => setRedeemAmount(e.target.value)}
            placeholder={`مثال: ${minR}`}
            dir="ltr"
            className="flex-1 px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none font-bold text-center"
            data-testid="loyalty-redeem-input"
          />
          <button
            onClick={redeem}
            disabled={busy || !canRedeem}
            className="px-4 py-3 rounded-xl bg-primary-brand text-white font-black disabled:opacity-60 flex items-center gap-1"
            data-testid="loyalty-redeem-btn"
          >
            <Sparkles size={15}/> {busy ? "جاري..." : "استبدال"}
          </button>
        </div>
        {msg && <div className="mt-3 text-sm bg-green-50 text-green-700 px-3 py-2 rounded-lg" data-testid="loyalty-msg">{msg}</div>}
        {err && <div className="mt-3 text-sm bg-red-50 text-red-600 px-3 py-2 rounded-lg" data-testid="loyalty-err">{err}</div>}
        {!canRedeem && !err && (
          <div className="mt-3 text-xs text-[var(--text-secondary)] bg-[var(--surface-alt)] p-3 rounded-lg">
            تحتاج <span className="font-bold text-primary-brand">{minR - state.points}</span> نقطة إضافية لتتمكن من الاستبدال. اطلب من مطاعم طلبية لتربح المزيد!
          </div>
        )}

        {/* Ledger */}
        {ledger.length > 0 && (
          <div className="mt-5 border-t border-[var(--border)] pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-black text-sm">آخر المعاملات</h4>
              {state.ledger.length > 5 && (
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-xs font-bold text-primary-brand"
                  data-testid="loyalty-toggle-ledger">
                  {showAll ? "عرض أقل" : `عرض الكل (${state.ledger.length})`}
                </button>
              )}
            </div>
            <ul className="space-y-2">
              {ledger.map((e) => (
                <li key={e.id} className="flex items-center gap-3 text-xs" data-testid={`loyalty-entry-${e.id}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    e.points > 0 ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"
                  }`}>
                    {e.points > 0 ? <ArrowUpRight size={14}/> : <ArrowDownRight size={14}/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{e.reason || (e.kind === "earn" ? "كسب" : e.kind === "redeem" ? "استبدال" : "تعديل")}</div>
                    <div className="text-[10px] text-[var(--text-secondary)]">{fmtDate(e.created_at)}</div>
                  </div>
                  <div className={`font-black shrink-0 ${e.points > 0 ? "text-green-600" : "text-red-500"}`}>
                    {e.points > 0 ? "+" : ""}{e.points}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
